import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatErr } from "../lib/api";
import { ScareLogo } from "../components/Brand";
import { GoogleButton, postLoginPath } from "../components/AuthBits";

export default function LoginPage() {
  const { login, resendVerification, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [err, setErr] = useState("");
  const [unverified, setUnverified] = useState(false);
  const [resent, setResent] = useState("");
  const [loading, setLoading] = useState(false);

  // Already signed in? Send them to the right home (super-admins → /super-admin).
  React.useEffect(() => {
    if (user) nav(postLoginPath(user), { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setUnverified(false); setResent(""); setLoading(true);
    try {
      const u = await login(email, password, remember);
      // Super-admins always land on the platform dashboard, ignoring any prior
      // "from" location (e.g. a bookmarked /dashboard) that would mislead the redirect.
      const dest = postLoginPath(u);
      nav(u.is_super_admin ? dest : (loc.state?.from || dest), { replace: true });
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === "email_not_verified") { setUnverified(true); setErr(detail.message); }
      else setErr(formatErr(detail) || e.message);
    } finally { setLoading(false); }
  };

  const resend = async () => {
    const r = await resendVerification(email);
    setResent(r.verification_url || "sent");
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-md st-card p-10 st-fade-up">
        <div className="flex justify-center mb-8"><ScareLogo size={36} /></div>
        <h1 className="text-2xl font-bold mb-1">Welcome back</h1>
        <p className="text-[#8a8a8a] mb-6">Sign in to manage your scare attraction.</p>

        <GoogleButton label="Continue with Google" />
        <div className="my-6 flex items-center gap-3">
          <div className="flex-1 st-divider" />
          <span className="text-xs text-[#7a7a7a] uppercase tracking-wider">or sign in with email</span>
          <div className="flex-1 st-divider" />
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div>
            <label className="text-sm text-[#bbb]">Email</label>
            <input data-testid="login-email" className="st-input mt-1" type="email" required
                   value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@scaretickets.co.uk" />
          </div>
          <div>
            <label className="text-sm text-[#bbb]">Password</label>
            <input data-testid="login-password" className="st-input mt-1" type="password" required
                   value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          </div>
          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center gap-2 cursor-pointer text-[#bbb]">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="login-remember" /> Remember me
            </label>
            <Link to="/forgot-password" className="st-link" data-testid="goto-forgot">Forgot password?</Link>
          </div>
          {err && <div data-testid="login-error" className="text-sm text-[#fca5a5] bg-[#2a1212] border border-[#5b1919] rounded-lg p-3">{err}</div>}
          {unverified && (
            <div className="text-sm text-[#e8c987] bg-[#1a1205] border border-[#3a2a0a] rounded-lg p-3" data-testid="login-unverified">
              <button type="button" className="st-link" onClick={resend} data-testid="login-resend">Resend verification email</button>
              {resent && resent !== "sent" && <div className="mt-2 break-all text-[10px] text-[#8a8a8a]">Dev link: <a className="st-link" href={resent} data-testid="login-resend-link">{resent}</a></div>}
              {resent === "sent" && <div className="mt-2 text-xs text-[#86efac]">Verification email sent.</div>}
            </div>
          )}
          <button data-testid="login-submit" disabled={loading} className="st-btn w-full justify-center">
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="text-center text-sm text-[#8a8a8a] mt-6">
          New attraction? <Link to="/signup" className="st-link" data-testid="goto-signup">Create an account</Link>
        </p>
      </div>
    </div>
  );
}
